/**
 * A/B de ORDEM do cadastro — signup-order-ab-2026-08 (front).
 *
 * Sorteio determinístico por patientId. 27 braços com tráfego igual:
 *
 *   - 24 ordens de nome/e-mail/CPF/telefone. Confirmação de e-mail SEMPRE
 *     imediatamente depois do e-mail (tela `/email/confirm`). Termos sempre
 *     por último. Ids: `nome_email_cpf_telefone`, `email_nome_telefone_cpf`,
 *     ..., 24 permutações no total.
 *   - `noconfirm_control` — ordem controle (nome → email → cpf → telefone →
 *     termos), SEM a tela de confirmação de e-mail.
 *   - `termos_first` — termos primeiro; depois nome → email → confirm →
 *     cpf → telefone.
 *   - `doc_caf` — foto de RG/CNH/CIN no início. Se o OCR trouxer nome+CPF
 *     válidos, essas telas são puladas; e-mail (com confirmação), telefone
 *     e termos completam. Se falhar, o front rebaixa para `noconfirm_control`
 *     (a decisão fica em src/DocumentIdForm.jsx).
 *
 * SUSPENSO (19/08/2026). `SIGNUP_ORDER_AB_LIVE` fica false: o CTA e o
 * roteador usam um braço fixo. Não religar sem pedido explícito do
 * stakeholder — o desenho nunca chegou a ser experimento medido (backend
 * off, front sorteava antes do kill switch).
 *
 * Braço fixo vigente desde 29/09/2026: `noconfirm_control` (nome → e-mail →
 * CPF → telefone → termos), SEM a tela de confirmação de e-mail. Ver
 * `SIGNUP_DEFAULT_VARIANT` para os números que motivaram a remoção.
 */

import { isValidEmail } from './patientContactValidation';

const KEY = 'signupOrderVariant';
const KEY_FOR = 'signupOrderVariantFor';

const FIELDS = ['nome', 'email', 'cpf', 'telefone'];

const ROUTE_BY_STEP = Object.freeze({
  nome: '/consulta',
  email: '/email',
  emailConfirm: '/email/confirm',
  cpf: '/cpf',
  telefone: '/telefone',
  termos: '/termos',
  documento: '/cadastro/documento',
});

export const SIGNUP_ROUTES = ROUTE_BY_STEP;

function permutations(arr) {
  if (arr.length <= 1) return [arr.slice()];
  const out = [];
  for (let i = 0; i < arr.length; i++) {
    const rest = arr.slice(0, i).concat(arr.slice(i + 1));
    for (const p of permutations(rest)) out.push([arr[i], ...p]);
  }
  return out;
}

function buildOrderVariants() {
  return permutations(FIELDS).map((perm) => ({
    id: perm.join('_'),
    steps: expandOrderSteps(perm, /* withEmailConfirm */ true),
  }));
}

function expandOrderSteps(perm, withEmailConfirm) {
  const steps = [];
  for (const field of perm) {
    steps.push(field);
    if (field === 'email' && withEmailConfirm) steps.push('emailConfirm');
  }
  steps.push('termos');
  return steps;
}

const ORDER_VARIANTS = buildOrderVariants();

const CONTROL_ORDER = ['nome', 'email', 'cpf', 'telefone'];

const SPECIAL_VARIANTS = Object.freeze({
  noconfirm_control: {
    id: 'noconfirm_control',
    steps: expandOrderSteps(CONTROL_ORDER, /* withEmailConfirm */ false),
  },
  termos_first: {
    id: 'termos_first',
    steps: (() => {
      const s = expandOrderSteps(CONTROL_ORDER, /* withEmailConfirm */ true);
      return ['termos', ...s.filter((x) => x !== 'termos')];
    })(),
  },
  doc_caf: {
    id: 'doc_caf',
    // OCR resolve nome+CPF; fallback para o controle acontece na tela do
    // documento se falhar (setSignupVariant para noconfirm_control).
    steps: ['documento', 'email', 'emailConfirm', 'telefone', 'termos'],
  },
});

const VARIANTS_BY_ID = new Map();
for (const v of ORDER_VARIANTS) VARIANTS_BY_ID.set(v.id, v);
for (const v of Object.values(SPECIAL_VARIANTS)) VARIANTS_BY_ID.set(v.id, v);

export const SIGNUP_VARIANTS = Array.from(VARIANTS_BY_ID.keys());

/** Braço controle explícito: mesma ordem antiga do funil, com confirmação. */
export const SIGNUP_CONTROL = 'nome_email_cpf_telefone';

/**
 * Braço vigente com o A/B suspenso — ordem controle SEM a tela de
 * confirmação de e-mail.
 *
 * A auditoria de UX de 29/09/2026 mediu o custo da tela: 463 usuários (9,7%
 * da coorte) abandonaram o funil no passo de e-mail, com `/email/confirm`
 * como última rota em 228 deles e `email_confirmed_double_entry` como último
 * evento em 199. No mesmo período a tela barrou 37 typos
 * (`email_confirm_typo_blocked`). A validação de domínio parecido do
 * `/email` (`suggestEmailDomain`) continua ativa e cobre o caso real de erro
 * de digitação sem cobrar a etapa de todo mundo.
 */
export const SIGNUP_DEFAULT_VARIANT = 'noconfirm_control';

/**
 * Kill switch de produto. False = ninguém é sorteado; `getSignupVariant` e
 * `assignSignupVariant` devolvem `SIGNUP_DEFAULT_VARIANT`. O hasher abaixo
 * existe só para os testes do roteador.
 */
export const SIGNUP_ORDER_AB_LIVE = false;

function currentPatientId() {
  try {
    return (
      sessionStorage.getItem('patientId') ||
      localStorage.getItem('patientId') ||
      ''
    );
  } catch {
    return '';
  }
}

function fnv1a32(str) {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return h >>> 0;
}

/**
 * Hash uniforme 0..N-1 por patientId. Determinístico entre navegadores
 * (localStorage não sincroniza, mas o backend também recomputa por patientId).
 */
function bucketFor(patientId) {
  const seed = `signup-order-ab-2026-08:${String(patientId || '')}`;
  const roll = fnv1a32(seed);
  return roll % SIGNUP_VARIANTS.length;
}

/** Sorteio bruto (ignora o kill switch). Só para teste e auditoria. */
export function hashSignupVariant(patientId) {
  const pid = String(patientId || '');
  if (!pid) return SIGNUP_CONTROL;
  const idx = bucketFor(pid);
  return SIGNUP_VARIANTS[idx] || SIGNUP_CONTROL;
}

export function assignSignupVariant(patientId) {
  if (!SIGNUP_ORDER_AB_LIVE) return SIGNUP_DEFAULT_VARIANT;
  return hashSignupVariant(patientId);
}

export function setSignupVariant(variant, patientId) {
  const v = String(variant || '');
  if (!VARIANTS_BY_ID.has(v)) return;
  const pid = String(patientId || currentPatientId() || '');
  if (!pid) return;
  try {
    localStorage.setItem(KEY, v);
    localStorage.setItem(KEY_FOR, pid);
  } catch {}
}

export function getSignupVariantRaw() {
  try {
    const pid = currentPatientId();
    if (!pid) return null;
    if (localStorage.getItem(KEY_FOR) !== pid) return null;
    const v = localStorage.getItem(KEY);
    return VARIANTS_BY_ID.has(v) ? v : null;
  } catch {
    return null;
  }
}

export function getSignupVariant() {
  if (!SIGNUP_ORDER_AB_LIVE) return SIGNUP_DEFAULT_VARIANT;
  return getSignupVariantRaw() || SIGNUP_CONTROL;
}

export function clearSignupVariant() {
  try {
    localStorage.removeItem(KEY);
    localStorage.removeItem(KEY_FOR);
  } catch {}
}

export function getSignupSteps(variant = getSignupVariant()) {
  const v = VARIANTS_BY_ID.get(variant) || VARIANTS_BY_ID.get(SIGNUP_DEFAULT_VARIANT);
  return v.steps.slice();
}

function routeToStep(pathname) {
  const p = String(pathname || '').replace(/\/+$/, '') || '/';
  for (const [step, route] of Object.entries(ROUTE_BY_STEP)) {
    if (route === p) return step;
  }
  return null;
}

/** Primeira rota do braço — para onde o CTA da Home leva. */
export function firstSignupRoute(variant = getSignupVariant()) {
  const steps = getSignupSteps(variant);
  const step = steps[0];
  return ROUTE_BY_STEP[step] || ROUTE_BY_STEP.nome;
}

/**
 * Próxima rota depois de concluir a etapa `currentStep`. Se a etapa não
 * pertence ao braço (ex.: paciente chegou de deep link fora da ordem), pula
 * para a próxima etapa faltante do braço.
 */
export function nextSignupRoute(currentStep, variant = getSignupVariant()) {
  const steps = getSignupSteps(variant);
  const idx = steps.indexOf(currentStep);
  if (idx >= 0 && idx < steps.length - 1) {
    return ROUTE_BY_STEP[steps[idx + 1]];
  }
  // Fim do braço: o último step é sempre `termos`; o LevelUpScreen do
  // TermsOfUse decide entre /triagem e /consulta/chat.
  return null;
}

export function nextSignupRouteFromPathname(pathname, variant = getSignupVariant()) {
  const step = routeToStep(pathname);
  if (!step) return null;
  return nextSignupRoute(step, variant);
}

/** Rota anterior (para o botão voltar). Null se está no primeiro passo. */
export function prevSignupRoute(currentStep, variant = getSignupVariant()) {
  const steps = getSignupSteps(variant);
  const idx = steps.indexOf(currentStep);
  if (idx > 0) return ROUTE_BY_STEP[steps[idx - 1]];
  return null;
}

export function prevSignupRouteFromPathname(pathname, variant = getSignupVariant()) {
  const step = routeToStep(pathname);
  if (!step) return null;
  return prevSignupRoute(step, variant);
}

/**
 * Existe rascunho de e-mail esperando confirmação? Mesma leitura e mesma
 * exigência de validade do `EmailConfirmForm`: rascunho inválido também faz a
 * tela redirecionar, então não conta como passo alcançável.
 */
function hasPendingEmailDraft() {
  try {
    const draft =
      sessionStorage.getItem('patientEmailDraft')
      || localStorage.getItem('patientEmailDraft');
    return isValidEmail(String(draft || '').trim().toLowerCase());
  } catch {
    return false;
  }
}

/**
 * Rota anterior que o paciente consegue de fato abrir. `/email/confirm` só
 * renderiza enquanto existe `patientEmailDraft`; com o e-mail já confirmado ela
 * redireciona para `/email`, e o Voltar do CPF devolvia o paciente ao começo do
 * passo de e-mail. É a armadilha do Leandro (19/08/2026) no sentido inverso.
 */
export function prevReachableSignupRoute(currentStep, variant = getSignupVariant()) {
  const prev = prevSignupRoute(currentStep, variant);
  if (prev !== ROUTE_BY_STEP.emailConfirm || hasPendingEmailDraft()) return prev;
  return prevSignupRoute('emailConfirm', variant);
}

/**
 * Progresso (currentStep, totalSteps) para o ProgressBar de acordo com o
 * braço. A tela de documento conta como 1 (nome+CPF juntos), então braços
 * com `doc_caf` têm menos etapas visuais.
 */
export function signupProgress(currentPathname, variant = getSignupVariant()) {
  const steps = getSignupSteps(variant);
  const step = routeToStep(currentPathname);
  const idx = step ? steps.indexOf(step) : -1;
  const total = steps.length;
  const currentStep = idx >= 0 ? idx + 1 : Math.max(1, total);
  return { currentStep, totalSteps: total };
}

/** Está no primeiro passo do braço? (usado no back button) */
export function isFirstStep(pathname, variant = getSignupVariant()) {
  const step = routeToStep(pathname);
  if (!step) return false;
  const steps = getSignupSteps(variant);
  return steps[0] === step;
}

/** Braços onde a tela `/email/confirm` faz parte do fluxo. */
export function usesEmailConfirm(variant = getSignupVariant()) {
  return getSignupSteps(variant).includes('emailConfirm');
}

/**
 * Próxima rota DEPOIS do e-mail já confirmado (atalho "mesmo endereço" ou
 * braço sem /email/confirm). NÃO usar nextSignupRoute('email'): na maioria
 * dos braços isso aponta de volta para /email/confirm. Sem rascunho, a
 * tela de confirmação redireciona para /email e o paciente fica em loop
 * (Leandro 19/08/2026 — braço email_telefone_cpf_nome).
 */
export function nextRouteAfterEmailConfirmed(variant = getSignupVariant()) {
  if (usesEmailConfirm(variant)) {
    return nextSignupRoute('emailConfirm', variant);
  }
  return nextSignupRoute('email', variant);
}

/** Braço `doc_caf`? (tela `/cadastro/documento`). */
export function usesDocumentOcr(variant = getSignupVariant()) {
  return variant === 'doc_caf';
}

/** Braço com termos no início? */
export function isTermsFirst(variant = getSignupVariant()) {
  return variant === 'termos_first';
}

/** Passo atual do braço, se a rota casar; null caso contrário. */
export function stepForRoute(pathname) {
  return routeToStep(pathname);
}
